// Date checks on the CPR card, TB result and nursing license an applicant
// uploads. These used to block the application form outright with one
// generic message, which told nobody which date was wrong and stopped
// applicants submitting at all (2026-10-16). They now warn — the applicant
// as they type, the reviewer on the application and the candidate — and
// never block. Checks run against today, so a card that was fine when the
// application came in shows as expired once it is.
//
// TB is the one recorded by test date, not expiry: result documents carry
// the date the test was given or read, and screening is good for 12 months
// from it. The commonest mistake is typing the "next due" date from the
// card, which is why a future TB date gets its own message.

export const TB_VALID_MONTHS = 12;

const pad = (n) => String(n).padStart(2, '0');
// Local calendar date, not UTC — after 8pm Eastern toISOString() is already
// tomorrow.
export const localToday = (now = new Date()) =>
  `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

const addMonths = (iso, months) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1 + months, d);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

// A browser date input will happily hold year 0026 or 20266.
const implausible = (iso) => {
  const y = Number(iso.slice(0, 4));
  return !/^\d{4}-\d{2}-\d{2}$/.test(iso) || y < 1980 || y > 2100;
};

const MESSAGES = {
  cpr_expired: {
    en: (f) => `This CPR card expired on ${f.date}. If that is the date the card was issued, enter the expiry date instead; otherwise you will need a current card.`,
    es: (f) => `Esta tarjeta de RCP venció el ${f.date}. Si esa es la fecha de emisión, ingrese la fecha de vencimiento; de lo contrario necesitará una tarjeta vigente.`,
    reviewer: (f) => `CPR card expired ${f.date} — or the applicant entered the issue date.`,
  },
  license_expired: {
    en: (f) => `This license expired on ${f.date}. If that is the date it was issued, enter the expiry date instead; otherwise you will need a current license.`,
    es: (f) => `Esta licencia venció el ${f.date}. Si esa es la fecha de emisión, ingrese la fecha de vencimiento; de lo contrario necesitará una licencia vigente.`,
    reviewer: (f) => `License expired ${f.date} — or the applicant entered the issue date.`,
  },
  tb_future: {
    en: (f) => `${f.date} is in the future. Enter the date the TB test was given or read — not the expiry or "next due" date.`,
    es: (f) => `${f.date} es una fecha futura. Ingrese la fecha en que se hizo o se leyó la prueba de TB, no la fecha de vencimiento.`,
    reviewer: (f) => `TB test date ${f.date} is in the future — probably the "next due" date, not the test date.`,
  },
  tb_old: {
    en: (f) => `This TB test is more than 12 months old (it lapsed on ${f.lapsed}). You will need a newer test.`,
    es: (f) => `Esta prueba de TB tiene más de 12 meses (venció el ${f.lapsed}). Necesitará una prueba más reciente.`,
    reviewer: (f) => `TB test ${f.date} is over 12 months old — lapsed ${f.lapsed}.`,
  },
  implausible: {
    en: (f) => `${f.date} doesn't look like a real date — please check the year.`,
    es: (f) => `${f.date} no parece una fecha válida; revise el año.`,
    reviewer: (f) => `${f.label} date ${f.date} doesn't look like a real date.`,
  },
};

const LABELS = { cpr_expiry_date: 'CPR', tb_test_date: 'TB', license_expiry_date: 'License' };

/**
 * Problems with the credential dates on an application, keyed by field.
 * `licenseRequired` false skips the license (PCA, office, other).
 * Missing dates are not reported here — that is a missing-field question.
 * Returns [{ field, code, date, lapsed? }].
 */
export function credentialDateFlags(dates, { licenseRequired = true, today = localToday() } = {}) {
  const flags = [];
  const check = (field, fn) => {
    const v = dates?.[field];
    if (!v) return;
    if (implausible(v)) { flags.push({ field, code: 'implausible', date: v, label: LABELS[field] }); return; }
    const f = fn(v);
    if (f) flags.push({ field, date: v, ...f });
  };
  check('cpr_expiry_date', (v) => (v < today ? { code: 'cpr_expired' } : null));
  check('tb_test_date', (v) => {
    if (v > today) return { code: 'tb_future' };
    const lapsed = addMonths(v, TB_VALID_MONTHS);
    return lapsed <= today ? { code: 'tb_old', lapsed } : null;
  });
  if (licenseRequired) check('license_expiry_date', (v) => (v < today ? { code: 'license_expired' } : null));
  return flags;
}

// MM/DD/YYYY, as the browser's date box shows it to a US applicant.
const shown = (iso) => (/^\d{4}-\d{2}-\d{2}$/.test(iso || '') ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : iso);

/** The message for one flag — `lang` 'en' | 'es' for the applicant, or 'reviewer'. */
export const flagMessage = (flag, lang = 'en') => {
  const m = MESSAGES[flag.code];
  return (m[lang] || m.en)({ ...flag, date: shown(flag.date), lapsed: shown(flag.lapsed) });
};
